<template>
  <v-form ref="formRef" class="lighting-shared-form method-form" @submit.prevent="">
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-floor-plan</v-icon>
        <span>Zonendaten</span>
      </div>

      <!-- Zone -->
      <v-autocomplete
        v-model="form.zone"
        :error-messages="validationErrors?.zone"
        label="Zone"
        :items="zones"
        item-title="name"
        item-value="id"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.zone" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.zone }}</span>
          </v-tooltip>
        </template>
      </v-autocomplete>

      <!-- Fläche -->
      <v-text-field
        v-model.number="form.area"
        type="number"
        :error-messages="validationErrors?.area"
        label="Fläche"
        suffix="m²"
        :hint="hint.area"
        validate-on-blur
        class="mb-3 ml-8"
      />

      <!-- Austausch -->
      <v-autocomplete
        v-model="form.change"
        :error-messages="validationErrors?.change"
        label="Austausch"
        :items="changeTypes"
        item-title="name"
        item-value="id"
        :disabled="!form.zone"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.change" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.change }}</span>
          </v-tooltip>
        </template>
      </v-autocomplete>
    </div>
  </v-form>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import api from '@/plugins/axios'
import { useBaseForm } from '@/composables/useBaseForm'

/* Props expected by parent (action.vue) */
const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  reference:  { type: Boolean, default: false },
  planned:    { type: Boolean, default: false },
  otherData:  { type: Object, default: () => ({}) },
  sharedData: { type: Object, default: () => ({}) },
  validationErrors: { type: Object, default: () => ({}) },
  methodValue: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue'])

/* Field mapping (frontend -> backend keys) */
const fields = {
  zone:   'zone',
  area:   'area',
  change: 'change',
}

/* useBaseForm — mark as SHARED form */
const {
  form,
  hint,
  fetchHints,
} = useBaseForm({
  value: computed(() => props.modelValue),
  fields,
  defaults: {},
  reference: computed(() => props.reference),
  planned:   computed(() => props.planned),
  shared:    computed(() => true), // <-- important: shared section
  otherData: computed(() => props.otherData),
  sharedData:computed(() => props.sharedData),
  methodValue: computed(() => props.methodValue),
  emit: (event, payload) => emit(event, payload),
})

/* Local lists */
const zones = ref([])
const changeTypes = ref([
  { id: 'sys',  name: 'Leuchtensystem' },
  { id: 'bulb', name: 'Leuchtmittel'  },
])

/* API: load zones (updated endpoint — removed /api/v1) */
async function fetchZones() {
  try {
    const { data } = await api.get('/methods/lighting2/zone/', {
      params: {},
    })
    zones.value = data
  } catch {
    // Optional: toast / error indicator
  }
}

/* Expose for parent: validate + backend fields for shared error filtering */
const formRef = ref(null)
function validateForm() {
  return formRef.value?.validate?.()
}

defineExpose({
  validateForm,
  fields: Object.values(fields),
})

/* Lifecycle */
onMounted(async () => {
  await fetchZones()
  await fetchHints()
})
</script>

<style scoped lang="scss">
.lighting-shared-form {
  width: 100%;
}

/* Field styling */
.lighting-shared-form :deep(.v-field) {
  border-radius: 18px;
}

.lighting-shared-form :deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Section wrapper */
.method-section {
  margin-bottom: 0;
  padding: 1rem;
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.025);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
}

/* Section heading */
.method-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;
  border-radius: 18px;
  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  font-size: 1rem;
  font-weight: 700;
}

/* Nested/dependent fields */
.lighting-shared-form :deep(.ml-8) {
  margin-left: 1.5rem !important;
  padding-left: 1rem;
  border-left: 2px solid rgba(var(--v-theme-primary), 0.12);
}

/* Hint icons */
.lighting-shared-form :deep(.v-input__append),
.lighting-shared-form :deep(.v-field__append-inner) {
  align-items: center;
}

.lighting-shared-form :deep(.v-input__append .v-icon),
.lighting-shared-form :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

/* Disabled fields */
.lighting-shared-form :deep(.v-field--disabled) {
  opacity: 0.72;
}

@media (max-width: 700px) {
  .method-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .method-section-heading {
    align-items: flex-start;
    font-size: 0.95rem;
  }

  .lighting-shared-form :deep(.ml-8) {
    margin-left: 0.75rem !important;
    padding-left: 0.75rem;
  }
}
</style>
